import { useContext, useEffect, useRef, useState } from "react";
import {
  BotMessage,
  ChartMessage,
  ChatRecords,
  ChatSec,
  DefaultChat,
  DefaultChats,
  MessageContent,
  UserMessage,
} from "../styles/Chat.styled";
import { Spin } from "antd";
import { useLocation } from "react-router-dom";
import { fetchFiles, fetchRuns, fetchThreads } from "../api/fetch";
import { UsagesChart } from "../components/chart";
import { Plans } from "../components/plans";
import Record from "../components/record";
import ReactMarkdown from "react-markdown";
import { RootContext } from "../context/RootContext";
import useQuery from "../hooks/useQuery";

const defaultMessages = [
  {
    label:
      "Analyze electricity usage, suggest economical plan for my household.",
  },
  {
    label: "How can I review all electricity plans for my household?",
  },
  {
    label:
      "What should I consider when choosing electricity plans for my household?",
  },
  {
    label: "Which electricity provider offers the best plans for my household?",
  },
];

const LoadingList = [
  "Thought Process…",
  "Synchronizing the latest grid data...",
  "Receiving up-to-date data from main servers...",
  "Complete data synchronization is just a moment away...",
  "Compiling electricity usage trends for your area...",
  "Loading geolocation-specific energy details...",
  "Loading season-specific energy consumption patterns...",
  "Importing data from local energy providers...",
  "Gathering peak and off-peak electricity usage data...",
  "Cross-referencing your address data...",
  "Calculating energy usage...",
  "Processing real-time energy usage stats...",
  "Nearly there, refining the best energy advice for you...",
  "Cross-checking recommendations with current energy rates...",
  "Almost done! Preparing personalized energy saving advice...",
  "Just a moment more, ensuring the accuracy of the assessment...",
];

export interface ProviderType {
  provider_id: string;
  provider_name: string;
}

export const ChatSection = () => {
  const query = useQuery();
  const origin = query.get("origin") || "";
  const threadId = query.get("thread_id") || "";
  const [chats, setChats] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState();
  // const [threadId, setThreadId] = useState<any>(null);
  const [threadChange, setThreadChange] = useState(false);
  const [isFirst, setIsFirst] = useState(true);
  // const [paddingChats, setPaddingChats] = useState<any>([])
  const location = useLocation();
  const chatRef = useRef<HTMLDivElement | null>(null);
  const recordRef = useRef<any>(null);
  const [width, setWidth] = useState<any>();
  const [loadingText, setLoadingText] = useState<any>();
  const [netError, setNetError] = useState(false);
  const { providerData } = useContext<any>(RootContext);

  const resizeUpdate = (e: any) => {
    const w = e.target.innerWidth;
    setWidth(w);
  };

  useEffect(() => {
    let w = window.innerWidth;
    setWidth(w);

    window.addEventListener("resize", resizeUpdate);

    return () => {
      window.removeEventListener("resize", resizeUpdate);
    };
  }, []);

  const handleSaveChat = (val: any) => {
    const newChats = chats ? chats : [];
    newChats.push(val);
    if (val.type === "text" && val.role === "assistant") {
      if (typeof val.value === "string") {
        setVoiceMessage(val.value);
      } else {
        setVoiceMessage(val.value.text);
      }
    }
    if (val.type === "chart" && val.role === "assistant") {
      setVoiceMessage(val.value.text);
    }
    setChats([...newChats]);
    handleScroll();
    setLoading(false);
  };

  // useEffect(() => {
  //   if (location && location.search) {
  //     setThreadId(location.search.substring(1));
  //   }
  // }, [location]);

  const getHistoryChat = () => {
    if (!threadId) return;
    fetchThreads(threadId).then((res) => {
      if (res && res.length > 0) {
        splitThreads(res);
      }
    });
  };

  useEffect(() => {
    if (threadChange) return;
    getHistoryChat();
  }, [threadId]);

  const splitThreads = async (arr: any) => {
    let newChats: any = [];
    for (const obj of arr) {
      let threadArr = [];
      const { role, content, run_id } = obj;
      const textValue =
        content.find((item: any) => item.type === "text")?.text.value || "";
      const file_id =
        content.find((item: any) => item.type === "image_file")?.image_file
          ?.file_id || "";
      let img = "";
      if (file_id) {
        const imgRes = await fetchFiles(file_id);
        if (imgRes && imgRes.type === "data:image/png") {
          img = imgRes.image;
        }
      }
      if (run_id) {
        const res = await fetchRuns(run_id);
        if (res && res.length > 0) {
          res.map((item: any) => {
            if (item.function_name === "get_plan") {
              threadArr.unshift({
                role: role,
                value: { res: item.res, address: item.raw_address, img: img },
                type: "plan",
              });
            }

            if (item.function_name === "get_usage") {
              threadArr.unshift({
                role: role,
                value: { res: item.res, text: textValue, img: img },
                type: "chart",
              });
            }
          });
        }

        if (
          !res ||
          res.length <= 0 ||
          res.every((item: any) => !item || item.function_name !== "get_usage")
        ) {
          threadArr.push({
            role: role,
            value: { text: textValue, img: img },
            type: "text",
          });
        }
      } else {
        threadArr.push({
          role: role,
          value: { text: textValue, img: img },
          type: "text",
        });
      }
      newChats = newChats.concat(threadArr);
    }
    setChats(newChats);
  };

  const handleScroll = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleClickMessage = (val: any) => {
    if (recordRef.current) {
      handleSaveChat({ type: "text", value: val, role: "user" });
      setIsFirst(false);
      recordRef.current.handleClickMessage(val);
    }
  };

  useEffect(() => {
    if (chatRef.current) {
      handleScroll();
    }
  }, [chatRef, chats, loading]);

  useEffect(() => {
    if (!loading) return;
    let index = 1;
    const ChangeTextId = setInterval(() => {
      if (netError) {
        return setLoadingText("Network error, please try again");
      }
      if (index > LoadingList.length - 1) {
        setLoadingText(LoadingList[LoadingList.length - 1]);
      } else {
        setLoadingText(LoadingList[index]);
      }
      index = index + 1;
    }, 4000);

    return () => {
      clearInterval(ChangeTextId);
    };
  }, [loading]);

  useEffect(() => {
    if (chats && chats.length > 0) {
      const elements = document.getElementsByClassName(
        "message-react-mark-down"
      );
      Array.from(elements).forEach((element) => {
        const htmlContent = element.innerHTML;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;
        const anchors = tempDiv.querySelectorAll("a");
        anchors.forEach((anchor) => {
          anchor.setAttribute("target", "_blank");
        });
        element.innerHTML = tempDiv.innerHTML;
      });
    }
  }, [chats]);

  return (
    <ChatSec showMessage={chats.length > 0 || loading}>
      <ChatRecords ref={chatRef}>
        {chats &&
          chats.length > 0 &&
          chats.map((item: any, idx: any) => {
            const { type, value, role } = item;
            if (role === "assistant" && type === "text") {
              const { text, img } = value;
              return (
                <BotMessage key={idx}>
                  <p>{"EnergyGPT"}</p>
                  <MessageContent>
                    <ReactMarkdown className="message-react-mark-down">
                      {text}
                    </ReactMarkdown>
                    {img && <img src={`data:image/png;base64,${img}`} />}
                  </MessageContent>
                </BotMessage>
              );
            }

            if (role === "assistant" && type === "chart") {
              const { res, text } = value;
              const { usages, address, img } = res;
              return (
                <BotMessage key={idx}>
                  <p>Searching for Usage</p>
                  <MessageContent>
                    <UsagesChart usages={usages} address={address} />
                    <ReactMarkdown className="message-react-mark-down">
                      {text}
                    </ReactMarkdown>
                    {img && <img src={`data:image/png;base64,${img}`} />}
                  </MessageContent>
                </BotMessage>
              );
            }

            if (role === "assistant" && type === "plan") {
              const { res, address, img } = value;
              return (
                <BotMessage key={idx}>
                  <p>Searching for Plan</p>
                  <MessageContent>
                    <Plans plans={res} address={address} />
                    {img && <img src={`data:image/png;base64,${img}`} />}
                  </MessageContent>
                </BotMessage>
              );
            }

            if (role === "user") {
              let text = "";
              if (typeof value === "string") {
                text = value;
              } else {
                text = value.text;
              }
              return (
                <UserMessage key={idx}>
                  <p>{text}</p>
                </UserMessage>
              );
            }
          })}
        {loading && (
          <BotMessage key={0}>
            <div>
              <Spin style={{ marginRight: "10px" }} /> {loadingText}
            </div>
          </BotMessage>
        )}

        {isFirst && !loading && (
          <DefaultChats>
            <h3>👋 Welcome to {origin ? origin : 'EnergyGPT'}! </h3>
            <p>I am EnergyGPT from eIQdigital.</p>
            {defaultMessages.map((item, index) => (
              <DefaultChat
                key={index}
                onClick={() => handleClickMessage(item.label)}
              >
                {item.label}
              </DefaultChat>
            ))}
          </DefaultChats>
        )}
      </ChatRecords>
      <Record
        providerId={providerData?.provider_id || ""}
        handleSaveChat={(val: any) => {
          handleSaveChat(val);
          setIsFirst(false);
        }}
        handleChangeLoading={(val: any) => {
          setLoadingText("Thought Process…");
          setLoading(val);
          handleScroll();
        }}
        handleChangeError={(val: any) => {
          setNetError(val);
          if (val) {
            setLoadingText("Network error, please try again");
            setTimeout(() => setLoading(false), 3000);
          }
        }}
        id={threadId}
        handleThreadChange={(val: any) => setThreadChange(val)}
        ref={recordRef}
      />
    </ChatSec>
  );
};
